import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IDS } from "../constants/testIds";
import { TONES, playTone } from "../lib/tones";

export const CHANNEL_COLORS = ["#2563EB", "#16A34A", "#EA580C", "#0D9488", "#7C3AED", "#0F172A", "#CA8A04", "#DB2777"];

export default function ChannelEditDialog({ channel, open, onOpenChange, onSave }) {
  const [form, setForm] = useState(channel || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && channel) setForm({ ...channel }); }, [open, channel]);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  if (!channel) return null;

  const save = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      await onSave(channel.id, {
        name: form.name.trim(), description: form.description || "", color: form.color,
        alert_tone: form.alert_tone, tone_repeats: Number(form.tone_repeats) || 1, tone_volume: Number(form.tone_volume) || 0.8,
        zello_channel: form.zello_channel || "", priority: form.priority || "normal",
      });
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={IDS.channels.editDialog} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel settings</DialogTitle>
          <DialogDescription>Name, colour, alert tone and Zello mapping for “{channel.name}”.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label>Channel name</Label>
            <Input data-testid={IDS.channels.editName} value={form.name || ""} onChange={e => set("name")(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Input data-testid={IDS.channels.editDesc} value={form.description || ""} onChange={e => set("description")(e.target.value)} placeholder="Optional" />
          </div>
          <div className="grid gap-1.5">
            <Label>Accent colour</Label>
            <div className="flex gap-2 flex-wrap">
              {CHANNEL_COLORS.map(c => (
                <button key={c} data-testid={IDS.channels.editColor(c)} onClick={() => set("color")(c)} className={`h-8 w-8 rounded-full border-2 transition ${form.color === c ? "border-slate-900 scale-110" : "border-transparent"}`} style={{ background: c }} aria-label={c} />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Zello channel name</Label>
              <Input data-testid={IDS.channels.editZello} value={form.zello_channel || ""} onChange={e => set("zello_channel")(e.target.value)} placeholder={form.name} />
            </div>
            <div className="grid gap-1.5">
              <Label>Priority</Label>
              <Select value={form.priority || "normal"} onValueChange={set("priority")}>
                <SelectTrigger data-testid={IDS.channels.editPriority}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High (pinned first)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 grid gap-3">
            <div className="overline">Alert tone</div>
            <div className="flex items-center gap-2">
              <Select value={form.alert_tone || "two_tone"} onValueChange={set("alert_tone")}>
                <SelectTrigger data-testid={IDS.channels.editTone} className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TONES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid={IDS.channels.editTonePreview} size="sm" variant="outline" className="gap-1.5" onClick={() => playTone(form.alert_tone || "two_tone", Number(form.tone_repeats) || 1, Number(form.tone_volume) || 0.8)}>
                <Play className="h-3.5 w-3.5" /> Preview
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 -mt-1">{TONES[form.alert_tone || "two_tone"].desc}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label className="text-xs">Repeats: {form.tone_repeats || 1}</Label>
                <Slider data-testid={IDS.channels.editToneRepeats} min={1} max={5} step={1} value={[Number(form.tone_repeats) || 1]} onValueChange={([v]) => set("tone_repeats")(v)} />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Volume: {Math.round((form.tone_volume ?? 0.8) * 100)}%</Label>
                <Slider data-testid={IDS.channels.editToneVolume} min={0.1} max={1} step={0.1} value={[Number(form.tone_volume ?? 0.8)]} onValueChange={([v]) => set("tone_volume")(v)} />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid={IDS.channels.editSave} onClick={save} disabled={saving || !form.name?.trim()}>{saving ? "Saving…" : "Save channel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
